import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import _ from 'lodash';
import * as actions from '../../redux/actions';
import { Loading } from '../../components/Loading';
import {
  SUBMENU_ENTRIES_BY_MODULE,
  SIDEBAR_MODULE_TITLE,
} from '../../constants/sidebarNavConfig';
import { showAlert } from '../../utils/functions/globalDialog';

const SELF_HREF = '/management/menu-visibility';

export default function MenuVisibilitySettings({ title }) {
  const dispatch = useDispatch();
  const info = useSelector((state) => state.info);
  const [hiddenDraft, setHiddenDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(actions.getInformation());
  }, []);

  useEffect(() => {
    if (info?.id) {
      setHiddenDraft(info.setting?.hidden_menus || []);
    }
  }, [info?.id]);

  const handleToggle = (href) => {
    if (href === SELF_HREF) return;
    setHiddenDraft((prev) => (
      prev.includes(href) ? prev.filter((h) => h !== href) : [...prev, href]
    ));
  };

  const handleModuleToggle = (mod) => {
    const moduleHrefs = (SUBMENU_ENTRIES_BY_MODULE[mod] || [])
      .map((m) => m.href)
      .filter((h) => h !== SELF_HREF);
    const allVisible = moduleHrefs.every((h) => !hiddenDraft.includes(h));
    if (allVisible) {
      setHiddenDraft((prev) => _.uniq([...prev, ...moduleHrefs]));
    } else {
      setHiddenDraft((prev) => prev.filter((h) => !moduleHrefs.includes(h)));
    }
  };

  const handleSave = async () => {
    if (!info?.id) return;
    setSaving(true);
    try {
      await dispatch(actions.editOneInformation(info.id, {
        setting: {
          ...info.setting,
          hidden_menus: hiddenDraft.filter((h) => h !== SELF_HREF),
        },
      }));
      await dispatch(actions.getInformation());
      showAlert('บันทึกสำเร็จ');
    } catch (_error) {
      showAlert('บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  if (!info?.id) {
    return <Loading />;
  }

  const modules = Object.keys(SUBMENU_ENTRIES_BY_MODULE);

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{title || 'จัดการเมนู'}</Typography>
          <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
            เมนูที่ไม่ติ๊ก จะไม่แสดงใน sidebar สำหรับทุกคน
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'กำลังบันทึก...' : 'บันทึก'}
        </Button>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {modules.map((mod) => {
          const items = SUBMENU_ENTRIES_BY_MODULE[mod] || [];
          const hrefs = items.map((m) => m.href).filter((h) => h !== SELF_HREF);
          const visibleCount = hrefs.filter((h) => !hiddenDraft.includes(h)).length;
          const allVisible = visibleCount === hrefs.length;
          const someVisible = visibleCount > 0 && !allVisible;
          return (
            <Paper key={mod} variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
              <ListItemButton dense onClick={() => handleModuleToggle(mod)} sx={{ bgcolor: '#fafafa', py: 1 }}>
                <Checkbox
                  size="small"
                  checked={allVisible}
                  indeterminate={someVisible}
                  onChange={() => handleModuleToggle(mod)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0, mr: 1.5 }}
                />
                <ListItemText
                  primary={<Typography variant="body2" sx={{ fontWeight: 700 }}>{SIDEBAR_MODULE_TITLE[mod] || mod}</Typography>}
                  secondary={<Typography variant="caption" color="text.secondary">{visibleCount}/{hrefs.length} เมนูที่แสดง</Typography>}
                />
              </ListItemButton>
              <Divider />
              {items.map((item) => {
                const isSelf = item.href === SELF_HREF;
                return (
                  <ListItemButton
                    key={item.href}
                    dense
                    disabled={isSelf}
                    onClick={() => handleToggle(item.href)}
                    sx={{ pl: 4.5, py: 0.5 }}
                  >
                    <Checkbox
                      size="small"
                      checked={isSelf || !hiddenDraft.includes(item.href)}
                      disabled={isSelf}
                      onChange={() => handleToggle(item.href)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ p: 0, mr: 1.5 }}
                    />
                    <ListItemText
                      primary={<Typography variant="body2">{item.title}</Typography>}
                      secondary={isSelf
                        ? <Typography variant="caption" sx={{ color: '#bdbdbd' }}>หน้านี้ต้องแสดงเสมอ</Typography>
                        : <Typography variant="caption" sx={{ color: '#bdbdbd' }}>{item.href}</Typography>}
                    />
                  </ListItemButton>
                );
              })}
            </Paper>
          );
        })}
      </Box>
    </div>
  );
}
